import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, History as HistoryIcon } from '@mui/icons-material';
import { useGetOrderEntryProductAutocomplete } from '../../hooks/api/oms';

interface ProductSearchResult {
  partNumber: string;
  description: string;
  status: string;
  listPrice?: number;
  leadTime?: number;
}

interface ProductSearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (product: ProductSearchResult) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

const ProductSearchAutocomplete: React.FC<ProductSearchAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Search by part number',
  label = 'Product Search',
  disabled = false,
  fullWidth = false,
  size = 'medium',
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get search history from localStorage
  useEffect(() => {
    const history = localStorage.getItem('productSearchHistory');
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (error) {
        console.error('Error parsing search history:', error);
      }
    }
  }, []);

  // Update search term when value prop changes
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Use the new autocomplete hook
  const { data, isLoading, error } = useGetOrderEntryProductAutocomplete(searchTerm);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setShowSuggestions(true);
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleSelectProduct = (partNumber: string) => {
    // Create a minimal product result for the onSelect callback
    const product: ProductSearchResult = {
      partNumber,
      description: '', // Will be filled when product is actually selected
      status: 'A',
    };
    
    onSelect(product);
    setSearchTerm(partNumber);
    onChange(partNumber);
    setShowSuggestions(false);
    addToSearchHistory(partNumber);
  };

  const handleSelectHistory = (historyItem: string) => {
    setSearchTerm(historyItem);
    onChange(historyItem);
    setShowSuggestions(false);
  };

  const addToSearchHistory = (term: string) => {
    if (term.trim()) {
      const newHistory = [term, ...searchHistory.filter(item => item !== term)].slice(0, 10);
      setSearchHistory(newHistory);
      localStorage.setItem('productSearchHistory', JSON.stringify(newHistory));
    }
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    onChange('');
    setShowSuggestions(false);
  };

  const renderSuggestions = () => {
    if (!showSuggestions) return null;

    const hasResults = data?.partNumbers && data.partNumbers.length > 0;
    const hasHistory = searchHistory.length > 0;
    const hasSearchTerm = searchTerm.length >= 2;

    if (!hasResults && !hasHistory && !hasSearchTerm) return null;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          maxHeight: 200,
          overflow: 'auto',
          boxShadow: 4,
          border: '1px solid #e0e0e0',
          mt: 0.5,
          backgroundColor: 'white',
        }}
      >
        <List dense>
          {/* Search Results */}
          {hasResults && data.partNumbers.map((partNumber: string, index: number) => (
            <ListItem
              key={`${partNumber}-${index}`}
              onClick={() => handleSelectProduct(partNumber)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                },
              }}
            >
              <ListItemText 
                primary={partNumber}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 'bold',
                  color: '#000000'
                }}
                sx={{
                  '& .MuiListItemText-primary': {
                    color: '#000000 !important'
                  }
                }}
              />
            </ListItem>
          ))}

          {/* Search History */}
          {hasHistory && (!hasResults || data?.partNumbers?.length === 0) && (
            <>
              <ListItem sx={{ backgroundColor: '#f8f8f8' }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <HistoryIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        Recent Searches
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {searchHistory.map((historyItem, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleSelectHistory(historyItem)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    },
                  }}
                >
                  <ListItemText
                    primary={historyItem}
                    secondary="Click to search"
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: '#333'
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: '#666'
                    }}
                  />
                </ListItem>
              ))}
            </>
          )}

          {/* No Results */}
          {hasSearchTerm && !isLoading && (!hasResults || data?.partNumbers?.length === 0) && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.secondary">
                    {error ? 'Search failed. Please try again.' : 'No products found.'}
                  </Typography>
                }
              />
            </ListItem>
          )}

          {/* Loading State */}
          {isLoading && (
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Searching products...
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          )}

          {/* Error State */}
          {error && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="error">
                    Search failed. Please try again or contact support if the problem persists.
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Paper>
    );
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          ref={inputRef}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          label={label}
          disabled={disabled}
          fullWidth={false}
          size="small"
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isLoading && <CircularProgress size={16} color="inherit" />}
                {error && (
                  <Tooltip title="Search failed. Click to retry.">
                    <IconButton size="small" onClick={() => window.location.reload()} color="error">
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {searchTerm && (
                  <IconButton size="small" onClick={clearSearch}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
          sx={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              color: '#000000',
              '& fieldset': {
                borderColor: '#000000',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: '#000000',
                borderWidth: '1px',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#000000',
                borderWidth: '2px',
              },
              '& input': {
                color: '#000000',
                '&::placeholder': {
                  color: '#666666',
                  opacity: 0.7,
                },
              },
              '& .MuiInputAdornment-root': {
                color: '#666666',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#666666',
              '&.Mui-focused': {
                color: '#000000',
              },
              '&.MuiInputLabel-shrink': {
                color: '#666666',
              },
            },
            '& .MuiIconButton-root': {
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
            '& .MuiCircularProgress-root': {
              color: '#666666',
            },
          }}
        />
        {renderSuggestions()}
      </Box>
    </ClickAwayListener>
  );
};

export default ProductSearchAutocomplete; 